import React, { useState, useEffect } from 'react';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}
import './CookieBanner.css';

export interface CookiePreferences {
  necessary: boolean;
  registration: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'grented_cookie_consent';

export const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, can't be disabled
    registration: true, // Always true for registration functionality
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      initializeServices(savedPreferences);
    }
  }, []);

  const initializeServices = (prefs: CookiePreferences) => {
    // Initialize Google Analytics only if analytics consent given
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }

    // Initialize marketing tools only if marketing consent given
    if (prefs.marketing) {
      // Initialize marketing cookies/pixels
    }
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      registration: true,
      analytics: true,
      marketing: true
    };
    
    saveConsent(allAccepted);
    initializeServices(allAccepted);
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      registration: true,
      analytics: false,
      marketing: false
    };
    
    saveConsent(necessaryOnly);
    setShowBanner(false);
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    initializeServices(preferences);
    setShowBanner(false);
    setShowDetails(false);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
    setPreferences(prefs);
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-modal-overlay">
      <div className="cookie-modal">
        <div className="cookie-content">
          <h3>Consenso ai cookie</h3>
          
          {!showDetails ? (
            <>
              <p>
                Utilizziamo cookie tecnici necessari al funzionamento della piattaforma e per la registrazione/verifica account.
                I cookie opzionali (analitici e marketing) sono disattivati per impostazione predefinita e puoi abilitarli se lo desideri.
                Al momento, l’unico cookie utilizzato è quello necessario per completare la verifica dell’account in caso di chiusura della sessione.
              </p>
              <p>
                <a href="/privacy-policy" target="_blank" rel="noopener">
                  Leggi la Privacy Policy completa
                </a>
              </p>
              
              <div className="cookie-actions">
                <button 
                  className="btn-accept-all"
                  onClick={handleAcceptAll}
                >
                  Accetta tutti
                </button>
                <button 
                  className="btn-necessary"
                  onClick={handleAcceptNecessary}
                >
                  Solo necessari
                </button>
                <button 
                  className="btn-customize"
                  onClick={handleCustomize}
                >
                  Personalizza
                </button>
              </div>
            </>
          ) : (
            <>
              <h4>Personalizza le tue preferenze cookie</h4>
              
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <input 
                    type="checkbox" 
                    checked={preferences.necessary}
                    disabled
                    id="necessary"
                  />
                  <label htmlFor="necessary">
                    <strong>Cookie Necessari</strong> (Obbligatori)
                  </label>
                </div>
                <p>Cookie tecnici essenziali per login, sessioni e funzionalità base della piattaforma.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <input 
                    type="checkbox" 
                    checked={preferences.registration}
                    disabled
                    id="registration"
                  />
                  <label htmlFor="registration">
                    <strong>Cookie di Registrazione</strong> (Obbligatori)
                  </label>
                </div>
                <p>Cookie necessari per il processo di registrazione, verifica email e gestione account utente.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                    id="analytics"
                  />
                  <label htmlFor="analytics">
                    <strong>Cookie Analitici</strong>
                  </label>
                </div>
                <p>Google Analytics per statistiche anonime di utilizzo e miglioramento UX.</p>
              </div>

              <div className="cookie-category">
                <div className="cookie-category-header">
                  <input 
                    type="checkbox" 
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                    id="marketing"
                  />
                  <label htmlFor="marketing">
                    <strong>Cookie Marketing</strong>
                  </label>
                </div>
                <p>Cookie per personalizzazione contenuti e remarketing (disabilitati di default).</p>
              </div>

              <div className="cookie-actions">
                <button 
                  className="btn-save"
                  onClick={handleSavePreferences}
                >
                  Salva Preferenze
                </button>
                <button 
                  className="btn-back"
                  onClick={() => setShowDetails(false)}
                >
                  Indietro
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
